import {
  isCurrentSourceTransaction,
  shouldRemoveSource,
  retainSourceView,
  retainedSourceDevices
} from '#~/agent/broker.js';
/* eslint-disable import/no-internal-modules */
/* eslint-disable @typescript-eslint/no-use-before-define */
import type { Annotation } from '@studio/design-system/video-annotation';
import { ensureNotNil } from '@studio/js-utils';
import { generateId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import {
  StatusDiscovery,
  StatusDiscoveryConnectionLocal,
  StatusDiscoveryConnectionNetwork,
  StatusDiscoveryInfoMDNS,
  StatusDiscoveryInfo,
  type State
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { toObjectEntries, toObjectKeys, toObjectValues } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import { freeze } from 'immer';
import type { LiveKitImpl } from '../../livekit/livekit.js';
import type { Proxy, Transaction } from '#~/agent/index.js';
import { broker, locateProxy } from '#~/agent/index.js';
import type { ByteId } from '#~/api/index.js';
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import { transformAutoTrackingReservations } from '#~/auto-tracking/index.js';
import {
  annotationSubjectForAgentCameraAndWorker,
  embeddingBeaconSubjectForAgentCameraAndWorker
} from '#~/auto-tracking/provider/utils.js';
import { depot } from '#~/depot/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { isAudioDevice, isVideoDevice } from '#~/device/index.js';
import { syncPersistedTransitoryDevices } from '#~/device/transitory.js';
import { pendingAuthPlaceholders } from '#~/device/transitory-record.js';
import { locateLiveKit } from '#~/livekit/utils.js';
import { logger } from '#~/logger/index.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { comparatorFn } from '#~/models/index.js';
import { pbToJsDevice, pbToJsDiscovery } from '#~/protobuf/index.js';
import type { Room } from '#~/room/index.js';
import { workers } from '#~/workers/auto-tracking/worker.js';
import { activeWorkers, setLocateLiveKitFn } from './active-worker-registry.js';
import { AutoTrackingControl, Status } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { BrowserMediaDevice } from '#~/mediadevices/types.js';
import { discoverMediaDevices } from '#~/mediadevices/discovery.js';
import { Protocol } from '@studio/models/video';
import { Protocol as AudioProtocol } from '@studio/models/audio';
import { autoRestorePipelineForDeviceIfNecessaryFromURX } from '#~/mediadevices/auto-restore.js';
import { localDeviceIdURXMapping, tearDownPipelineIfNecessary } from '#~/mediadevices/pipeline.js';
import type { Configuration as LocalVideoConfig } from '@studio/models/video/local';
import type { Configuration as LocalAudioConfig } from '@studio/models/audio/local';
import { tick } from '#~/statequery/central-dispatch.js';

// yolo
setLocateLiveKitFn(locateLiveKit);

/** @internal */
const classify = (
  devices: Room['devices']
): { audioIds: Device['id'][]; videoIds: Device['id'][] } => {
  const deviceArray = toObjectValues(devices);

  return {
    audioIds: deviceArray
      .filter(isAudioDevice)
      .sort(comparatorFn)
      .map((device) => device.id),

    videoIds: deviceArray
      .filter(isVideoDevice)
      .sort(comparatorFn)
      .map((device) => device.id)
  };
};

/** @internal */
const syncDevices = (
  agentId: Agent['id'],
  room: Room,
  pbState: Pick<State, 'control' | 'status'>,
  proxy: T_Maybe<Proxy>,
  tx: Transaction
): void => {
  // Last observed rows survive uncertain absence; only validated current authority can remove them.
  const nextDevices: Room['devices'] = {};

  for (const key of Object.keys(pbState.control?.device ?? {})) {
    if (pbState.control?.device[key]?.urx === undefined) {
      continue;
    }

    // Transform device
    const jsDevice = pbToJsDevice(agentId, key, pbState, proxy?.embedded ?? false);

    if (!jsDevice) {
      continue;
    }

    if (!proxy || !shouldRemoveSource(proxy, tx, jsDevice.id)) nextDevices[jsDevice.id] = jsDevice;
  }

  // Copy over any devices that are not owned by this agent.
  for (const key of Object.keys(room.devices)) {
    const device = room.devices[<Device['id']>key];

    if (
      device !== undefined &&
      (device.agentId !== agentId || !proxy || !shouldRemoveSource(proxy, tx, device.id))
    ) {
      if (!nextDevices[<Device['id']>key]) {
        nextDevices[<Device['id']>key] = device;
        // F14: never retain on an optimistic transaction — an optimistic transient delete would
        // otherwise flash the row "Recovering" and leave a permanent retained entry behind.
        if (device.agentId === agentId && proxy && !tx.isOptimistic)
          retainSourceView(proxy, device);
      }
    }
  }

  // Run through each of the next devices.
  const updatedDevices: Device[] = [];
  const matchIds = new Set(toObjectKeys(room.devices));

  toObjectEntries(nextDevices).forEach(([deviceId, nextDevice]) => {
    // Is this a new device?
    if (!matchIds.has(deviceId)) {
      updatedDevices.push(nextDevice);

      return;
    }

    // This is an existing device.
    matchIds.delete(deviceId);

    // Has this device changed?
    if (!dequal(room.devices[deviceId], nextDevice)) {
      // Logger.debug(`Device changed: ${deviceId}`);

      updatedDevices.push(nextDevice);
    }
  });

  // Is there anything that needs to be changed?
  if (updatedDevices.length > 0 || matchIds.size) {
    depot.getState().produce((draft) => {
      const draftRoom = ensureNotNil(draft.rooms[room.id]);

      // Add the new devices.
      updatedDevices.forEach((device) => {
        draftRoom.devices[device.id] =
          // Pre-freeze state - https://bit.ly/3TIU4La
          freeze(device);
      });

      // Remove any devices that are no longer present.
      for (const deviceId of matchIds) {
        delete draftRoom.devices[deviceId];
      }

      const { audioIds, videoIds } = classify(draftRoom.devices);

      if (!dequal(draftRoom.audioIds, audioIds)) {
        draftRoom.audioIds = audioIds;
      }

      if (!dequal(draftRoom.videoIds, videoIds)) {
        draftRoom.videoIds = videoIds;
      }
    });
  }
};

let lastDiscoverySync = 0;
//let browserDiscoveries: BrowserMediaDevice[] = [];

const transformBrowserDiscoveryToDiscovery = (
  agentId: Agent['id'],
  discovery: BrowserMediaDevice
): Discovery =>
  ({
    agentId: agentId,
    id: <Discovery['id']>discovery.id,
    name: discovery.label,

    ...(discovery.kind === 'video'
      ? {
          video: {
            driver: 'browsermedia',
            path: discovery.persistentID,
            protocol: Protocol.Local
          }
        }
      : {}),

    ...(discovery.kind === 'audio'
      ? {
          audio: {
            driver: 'browsermedia',
            path: discovery.persistentID,
            protocol: Protocol.Local
          }
        }
      : {})
  }) as Discovery;

export const jsToPbDiscovery = (discovery: BrowserMediaDevice): T_Maybe<StatusDiscovery> => {
  const { id, ...rest } = discovery;

  if (!id) {
    return undefined;
  }

  const statusDiscovery = StatusDiscovery.fromPartial({
    source: Protocol.Local,
    local: StatusDiscoveryConnectionLocal.fromPartial({
      api: rest.kind === 'audio' ? 'browsermedia-audio' : 'browsermedia-video',
      id: rest.persistentID,
      name: rest.label
    })
  });

  return statusDiscovery;
};

export const transformStatusBrowserDiscoveryToDiscovery = (
  agentId: Agent['id'],
  discovery: StatusDiscovery
): T_Maybe<Discovery> => {
  const { local, source } = discovery;

  if (source && local && local.id) {
    const kind: 'audio' | 'video' = local.api === 'browsermedia-audio' ? 'audio' : 'video';

    if (kind === 'audio') {
      const audio: LocalAudioConfig = {
        driver: 'browsermedia',
        path: local.id,
        protocol: AudioProtocol.Local
      };

      return {
        agentId,
        id: <Discovery['id']>local.id,
        name: local.name!,
        audio
      };
    }

    if (kind === 'video') {
      const video: LocalVideoConfig = {
        driver: 'browsermedia',
        path: local.id,
        protocol: Protocol.Local
      };

      return {
        agentId,
        id: <Discovery['id']>local.id,
        name: local.name!,
        video
      };
    }
  }

  return undefined;
};

let syncBrowserDevicesCachedResult: T_Maybe<Discovery[]> = undefined;

const syncBrowserDevices = async (
  agentId: Agent['id'],
  proxy: T_Maybe<Proxy>
): Promise<Discovery[]> => {
  // Tray window skips discovery/advertising — the main window already does it (avoids double-firing).
  if (isElectronTrayWindow()) {
    return [];
  }

  if (Date.now() - lastDiscoverySync < 10_000) {
    return syncBrowserDevicesCachedResult ?? [];
  }

  lastDiscoverySync = Date.now();

  const browserDiscoveries = await discoverMediaDevices();

  const statusDiscoveries = browserDiscoveries
    .map((discovery) => jsToPbDiscovery(discovery))
    .filter(ensureNotNil);

  const statusDiscoveriesMap: Record<string, StatusDiscovery> = {};

  await Promise.all(
    statusDiscoveries.map(async (discovery) => {
      const id = <string>discovery?.local?.id;

      if (!id || !discovery) {
        return;
      }

      //const stableID = await stableIDForPersistentID(id);

      statusDiscoveriesMap[id] = discovery;
    })
  );

  // Merge mDNS + browser discoveries into one mutation (mutateAdvertisedDevices replaces the whole map).
  const mdnsDevices = await collectMdnsDevices().catch(() => {
    return {} as Record<string, StatusDiscovery>;
  });
  const mergedDiscoveries = { ...statusDiscoveriesMap, ...mdnsDevices };

  let status: number | undefined = undefined;
  try {
    status = await proxy?.mutateAdvertisedDevices(mergedDiscoveries);
  } catch (err) {
    logger.warning('mutateAdvertisedDevices threw', err);
  }

  if (status === -1) {
    lastDiscoverySync = new Date().getTime() - 10_000 + 5_000;
  }

  const transformedDiscoveries = browserDiscoveries.map((discovery) =>
    transformBrowserDiscoveryToDiscovery(agentId, discovery)
  );

  syncBrowserDevicesCachedResult = transformedDiscoveries;

  return transformedDiscoveries;
};

/**
 * TXT keys the mDNS scanner stamps onto `txtRecords` itself instead of reading them from the
 * wire, so they must never reach a fingerprint rule or the state-sync diff.
 */
const MDNS_TELEMETRY_TXT_KEYS = new Set(['lastSeen']);

/** True for a dotted-quad IPv4 literal — keeps an IP out of the forwarded mDNS `name` field. */
const isIPv4Literal = (s: string): boolean => /^(\d{1,3}\.){3}\d{1,3}$/.test(s);

/** Collects mDNS-discovered devices from Electron as StatusDiscovery entries. */
const collectMdnsDevices = async (): Promise<Record<string, StatusDiscovery>> => {
  // Query mDNS devices via the ElectronAPI shim ('mdns:getDiscoveredDevices' IPC).
  let devices: any = undefined;
  try {
    // Lazy import to avoid pulling Electron-only code into non-Electron builds.
    const { ElectronAPI } = await import('#~/electron/api.js');
    devices = await ElectronAPI?.mDNS?.getDiscoveredDevices?.();
  } catch (err) {
    logger.warning('collectMdnsDevices: failed to call ElectronAPI', err);
    return {};
  }
  const mdnsDiscoveries: Record<string, StatusDiscovery> = {};

  if (devices && Array.isArray(devices)) {
    for (const device of devices) {
      // Per-device try/catch: one malformed device must not drop the whole mDNS set.
      try {
        // Forward the device's mDNS TXT records to state sync via the `info` map on
        // each entry below. Previously omitted to trim state-sync traffic while
        // fingerprints matched on the instance name only; re-enabled now that the
        // encoder ships TXT-key fingerprint rules (its MatchMDNS iterates every info
        // entry). Only TXT is forwarded — the ARP-derived MAC lives on a separate
        // device field and is never stored in txtRecords, so no MAC surface returns.
        if (typeof device.address !== 'string' || device.address.length === 0) {
          // Skip devices without an IP — no stable key.
          logger.warning('collectMdnsDevices: device missing address, skipping', { device });
          continue;
        }
        const safeAddress = device.address.replace(/[.:]/g, '_');

        // One entry per serviceType, keyed by serviceType+IP; name = mDNS instance name, type = service type.
        const serviceTypes = device.serviceTypes ?? [];
        const instanceNames: Record<string, string> = device.instanceNames ?? {};
        // Flatten txtRecords (keyed by serviceType) into one key→value map for the
        // `info` field. Collapsing across service types is safe: on the rare shared
        // key, last write wins.
        const txtByServiceType: Record<string, Record<string, string>> = device.txtRecords ?? {};
        const flatTxt: Record<string, string> = {};
        for (const svcTxt of Object.values(txtByServiceType)) {
          for (const [txtKey, txtValue] of Object.entries(svcTxt ?? {})) {
            // Drop MDNS_TELEMETRY_TXT_KEYS: keys the mDNS scanner synthesizes rather than
            // reads off the wire. They are not device data, and because they carry a fresh
            // timestamp on every packet they made every entry differ from the last one, so
            // state sync could never dedupe an unchanged advertisement. Their real home is
            // the device-level field of the same name, which is untouched here.
            if (MDNS_TELEMETRY_TXT_KEYS.has(txtKey)) {
              continue;
            }
            flatTxt[txtKey] = txtValue;
          }
        }
        if (serviceTypes.length === 0) {
          // No service types yet — create a single entry keyed by IP only. Forward the mDNS
          // hostname as the name: for a host found only by the network scanner's reverse-DNS
          // query it is the sole identifying string the device gives us, and fingerprint rules
          // read `name`. Empty when no PTR has arrived yet.
          const key = `mdns_${safeAddress}`;
          mdnsDiscoveries[key] = StatusDiscovery.fromPartial({
            source: 'mdns',
            network: StatusDiscoveryConnectionNetwork.fromPartial({ host: device.address }),
            info: StatusDiscoveryInfo.fromPartial({
              mdns: StatusDiscoveryInfoMDNS.fromPartial({
                name:
                  typeof device.host === 'string' && !isIPv4Literal(device.host) ? device.host : '',
                type: '',
                info: flatTxt
              })
            })
          });
        } else {
          for (const svcType of serviceTypes) {
            const safeSvcType = svcType.replace(/[.:]/g, '_');
            const key = `mdns_${safeSvcType}_${safeAddress}`;
            // Prefer the PTR instance name, falling back to svcType.
            const instanceName = instanceNames[svcType] ?? svcType;
            mdnsDiscoveries[key] = StatusDiscovery.fromPartial({
              source: 'mdns',
              network: StatusDiscoveryConnectionNetwork.fromPartial({ host: device.address }),
              info: StatusDiscoveryInfo.fromPartial({
                mdns: StatusDiscoveryInfoMDNS.fromPartial({
                  name: instanceName,
                  type: svcType,
                  info: flatTxt
                })
              })
            });
          }
        }
      } catch (err) {
        logger.warning('collectMdnsDevices: skipping malformed device', {
          address: device?.address,
          error: err
        });
      }
    }
  }

  return mdnsDiscoveries;
};

/** @internal */
const syncDiscoveries = (
  agentId: Agent['id'],
  room: Room,
  proxy: T_Maybe<Proxy>,
  pbState: Pick<State, 'status' | 'control'>
): void => {
  // syncBrowserDevices merges mDNS devices into the same mutation.
  const browserDiscoveryPromise = syncBrowserDevices(agentId, proxy);

  // Synthesize the next set of discoveries.
  const nextDiscoveries: Room['discoveries'] = {};

  // Process encoder discoveries
  const encoderDiscoveries = pbState.status?.discovery ?? {};
  for (const key of Object.keys(encoderDiscoveries)) {
    const discovery = encoderDiscoveries[<Discovery['id']>key];

    if (!discovery) {
      continue;
    }

    // Transform discovery
    const jsDiscovery = pbToJsDiscovery(agentId, key as Discovery['id'], discovery);

    if (!jsDiscovery) {
      continue;
    }

    nextDiscoveries[jsDiscovery.id] = jsDiscovery;
  }

  // IMPORTANT: Preserve existing discoveries from OTHER agents first
  // This prevents sources from other bridges from disappearing during this agent's update
  const existingDiscoveries = room.discoveries || {};
  const preservedDiscoveries: Room['discoveries'] = {};
  let preservedCount = 0;

  for (const [key, discovery] of Object.entries(existingDiscoveries)) {
    if (discovery && discovery.agentId !== agentId) {
      preservedDiscoveries[<Discovery['id']>key] = discovery;
      preservedCount++;
    }
  }

  const accessReservations = pbState.control?.reservation ?? {};
  const reservationPromises = Object.keys(accessReservations).map(async (reservationId) => {
    try {
      const reservation = accessReservations[reservationId];

      if (!reservation) {
        return { reservationId, discoveries: [] };
      }

      const discoveries = reservation?.advertisedDiscoveries ?? {};

      const canPing = await proxy?.pingSession(reservationId);

      if (!canPing) {
        return { reservationId, discoveries: [] };
      }

      const reservationDiscoveries = [];
      for (const discoveryId of Object.keys(discoveries)) {
        const discovery = discoveries[discoveryId];

        if (!discovery) {
          continue;
        }

        const jsDiscovery = transformStatusBrowserDiscoveryToDiscovery(agentId, discovery);

        if (!jsDiscovery) {
          continue;
        }

        reservationDiscoveries.push(jsDiscovery);
      }

      return { reservationId, discoveries: reservationDiscoveries };
    } catch (error) {
      console.error('Error processing reservation', {
        reservationId,
        agentId,
        error
      });
      return { reservationId, discoveries: [] };
    }
  });

  // Use Promise.allSettled instead of Promise.all to handle partial failures gracefully
  Promise.allSettled([browserDiscoveryPromise, ...reservationPromises])
    .then((results) => {
      // Process browser discoveries (first result)
      const [browserResult, ...reservationResults] = results;

      if (browserResult.status === 'fulfilled') {
        const browserDiscoveries = browserResult.value;
        for (const discovery of browserDiscoveries) {
          const id = (discovery?.video as any)?.path ?? (discovery?.audio as any)?.path;

          if (!id) {
            continue;
          }

          nextDiscoveries[id] = discovery;
        }
      } else {
        console.error('Browser discovery failed', {
          agentId,
          error: browserResult.reason
        });
      }

      // Process reservation discoveries
      for (const result of reservationResults) {
        if (result.status === 'fulfilled') {
          const { discoveries } = result.value;
          for (const jsDiscovery of discoveries) {
            nextDiscoveries[jsDiscovery.id] = jsDiscovery;
          }
        } else {
          console.error('Reservation discovery failed', {
            agentId,
            error: result.reason
          });
        }
      }

      // Final merge: preserved discoveries from other agents + new discoveries from this agent
      const finalDiscoveries: Room['discoveries'] = {
        ...preservedDiscoveries,
        ...nextDiscoveries
      };

      // Apply the update atomically
      depot.getState().produce((draft) => {
        if (!draft.rooms[room.id]) {
          return;
        }

        draft.rooms[room.id]!.discoveries = finalDiscoveries;
      });
    })
    .catch((error) => {
      console.error('Critical error in discovery sync', { agentId, error });
      // In case of critical failure, at least preserve existing discoveries
      depot.getState().produce((draft) => {
        if (draft.rooms[room.id]) {
          // Keep existing discoveries to prevent total loss
        }
      });
    });
};

/**
 * Initializes the the specified room.
 *
 * @param proxies - Agent proxy map.
 * @param roomId - Room identifier.
 * @returns Control room state.
 * @see https://bit.ly/3SM6Rfr
 */
export const initialize = (proxies: Record<string, Proxy>, roomId: Room['id']): Room => {
  const devices: Room['devices'] = {};

  Object.values(proxies).forEach((proxy) => {
    for (const saved of retainedSourceDevices(proxy)) devices[saved.id] = saved;
    // Synchronize local react state with the agent state.
    for (const key of Object.keys(proxy.state.control?.device ?? {})) {
      // @ts-ignore
      const jsDevice = pbToJsDevice(proxy.id, key, proxy.state, proxy.embedded);

      if (!jsDevice) {
        logger.warning(`Unable to reconcile existing device: ${key}`);

        continue;
      }

      logger.debug(`Reconciling existing device: ${jsDevice.id}`);

      devices[jsDevice.id] = jsDevice;
    }

    proxy.mutateAutoTrackingReservations(
      proxy.state.control?.studio?.autoTrackingReservation ?? {}
    );
  });

  // Classify the devices.
  const { audioIds, videoIds } = classify(devices);

  // Get the room name from the roomRecords in the depot state if available
  const depotState = depot.getState();
  const roomName = depotState.roomRecords[roomId]?.name || 'My Studio';

  // Create the initial room state
  const room = {
    agentIds: Object.values(proxies).map(({ id }) => id),
    audioIds,
    devices,
    discoveries: {},
    id: roomId,
    liveKitSync: generateId(),
    name: roomName,
    videoIds
  };

  // Update the room name later when roomRecords are available
  if (!depotState.roomRecords[roomId]) {
    // Schedule a microtask to update the name once roomRecords are available
    queueMicrotask(() => {
      const checkAndUpdateName = () => {
        const currentState = depot.getState();
        const currentRoom = currentState.rooms[roomId];
        const record = currentState.roomRecords[roomId];

        if (currentRoom && record && currentRoom.name !== record.name) {
          currentState.produce((draft) => {
            if (draft.rooms[roomId]) {
              draft.rooms[roomId]!.name = record.name;
            }
          });
          return true;
        }
        return false;
      };

      // Try immediately
      if (!checkAndUpdateName()) {
        // If not successful, set up a MutationObserver to watch for changes to the depot state
        const intervalId = setInterval(() => {
          if (checkAndUpdateName() || !depot.getState().rooms[roomId]) {
            clearInterval(intervalId);
          }
        }, 500); // Check every 500ms

        // Clear the interval after 10 seconds to avoid memory leaks
        setTimeout(() => clearInterval(intervalId), 10_000);
      }
    });
  }

  return room;
};

/** Ids of the studio device records a transaction explicitly deleted. */
const studioDevicesUnsetBy = (tx: Transaction): ReadonlySet<string> => {
  const { appliedUnset, state } = tx;

  if (!appliedUnset || Object.keys(appliedUnset).length === 0 || !state) {
    return new Set();
  }

  // Decoded with the state's own schema, like every other read of the tree.
  return new Set(Object.keys(state.decode(appliedUnset).message.control?.studio?.device ?? {}));
};

/**
 * Synchronize room state with the agent.
 *
 * @param agentId - Agent identifier.
 * @param roomId - Room identifier.
 * @param tx - State sync transaction.
 */
export const synchronize = (agentId: Agent['id'], roomId: Room['id'], tx: Transaction): void => {
  const control = <State['control']>(<unknown>(tx.next?.message.control ?? {
    device: {}
  }));

  const status = <State['status']>(<unknown>(tx.next?.message.status ?? {}));

  const { produce, rooms } = depot.getState();

  // A throttled callback may outlive its room/account or reset generation.
  const proxy = broker.acquire(agentId);
  if (!proxy || proxy.roomId !== roomId || !isCurrentSourceTransaction(proxy, tx)) return;
  tick();

  if (proxy) {
    void proxy.emit('state.tx', tx);

    proxy.mutateAutoTrackingReservations(control?.studio?.autoTrackingReservation ?? {});

    // Update auto tracking state.
    const reservations = proxy.state.control?.studio?.autoTrackingReservation ?? {};

    produce((draft) => {
      draft.autoTrackingInstances[agentId] = freeze(
        transformAutoTrackingReservations(reservations, proxy)
      );
    });
  }

  produce((draft) => {
    if (draft.agents[agentId]?.status == 'online') {
      return;
    }

    if (
      (tx.isInit || tx.isReset || tx.isResync || tx.isAuthority) &&
      draft.agents[agentId] !== undefined
    ) {
      draft.agents[agentId]!.status = 'online';
    }
  });

  // Synchronize the room.
  const room = rooms[roomId];

  if (!room) {
    // Room has not appeared in the depot yet. It's okay - it will eventually show up.
    return;
  }

  // Synchronize devices.
  syncDevices(agentId, room, { control, status }, proxy, tx);

  // Pending cameras live as studio-only records, outside `room.devices`.
  syncPersistedTransitoryDevices(
    agentId,
    pendingAuthPlaceholders(agentId, roomId, control),
    Date.now(),
    studioDevicesUnsetBy(tx)
  );

  if (status || control) {
    // Synchronize discoveries.
    syncDiscoveries(agentId, room, proxy, { status, control });
  }

  for (const workerId of Object.keys(control?.studio?.autoTrackingWorker ?? {})) {
    const worker = control?.studio?.autoTrackingWorker[workerId];

    if (!worker) {
      continue;
    }

    const { state } = worker;

    if (!state) {
      continue;
    }

    const cameraId = state.control?.deviceId;

    synchronizeAutoTracking(agentId, cameraId, state.control, workerId);
  }

  void synchornizeBrowserDevices(agentId, control, status, () =>
    isCurrentSourceTransaction(proxy, tx)
  ).catch(logger.error);
};

const synchornizeBrowserDevices = async (
  agentId: Agent['id'],
  control: State['control'],
  _status: State['status'],
  current: () => boolean
): Promise<void> => {
  for (const deviceId of Object.keys(control?.device ?? {})) {
    if (!current()) return;
    const device = control?.device?.[deviceId];

    if (!device) {
      continue;
    }

    if (device?.urx?.includes('browsermedia://')) {
      await autoRestorePipelineForDeviceIfNecessaryFromURX(device?.urx!, agentId, deviceId);
    }
  }

  for (const deviceId of localDeviceIdURXMapping.keys()) {
    if (!current()) return;
    const id = locateProxy(deviceId as Device['id'])?.id;

    if (id !== agentId && id !== undefined) {
      continue;
    }

    if (!control?.device?.[deviceId]?.urx?.includes('browsermedia://')) {
      await tearDownPipelineIfNecessary(deviceId);
      if (!current()) return;
      localDeviceIdURXMapping.delete(deviceId);
    }
  }
};

const handleSelectRemoteAnnotation = (bbox: number[] | null, isHuman: boolean): void => {
  if (bbox != null) {
    // @ts-expect-error - This is a hack to expose the bounding box to the window object.
    window.__myBoundingBoxHuman = isHuman;
  }

  // @ts-expect-error - This is a hack to expose the bounding box to the window object.
  window.__myBoundingBoxRelative = bbox;

  if (isHuman) {
    // @ts-expect-error - This is a hack to expose the bounding box to the window object.
    window.__trackerMode = 2;
  }
};

let lastBboxSelection: Record<string, number[]> = {};

/**
 * Updates the room state for a specific auto tracking state.
 *
 * @param agentId - Agent identifier.
 * @param cameraId - Camera identifier.
 * @param state - Auto tracking state.
 * @param workerId - Worker identifier.
 */
export const synchronizeAutoTracking = (
  agentId: Agent['id'],
  cameraId: T_Maybe<Uint8Array>,
  state: T_Maybe<AutoTrackingControl>,
  workerId: string
): void => {
  if (agentId === undefined || workerId === undefined) {
    return;
  }

  const { produce } = depot.getState();

  produce((draft) => {
    const id = toHexFromId<Device['id']>(cameraId);

    if (!id) {
      return;
    }

    const instance = draft.autoTrackingInstances[agentId]?.[id];

    if (!instance) {
      return;
    }

    instance.state = state;

    draft.autoTrackingInstances[agentId]![id] = instance;
  });

  const last = lastBboxSelection[workerId];

  if (state?.selectionBbox === undefined && last !== undefined) {
    delete lastBboxSelection[workerId];
    handleSelectRemoteAnnotation(null, false);
  } else if (
    (last !== undefined && last?.[0] != state?.selectionBbox?.x1) ||
    last?.[2] != state?.selectionBbox?.x2 ||
    last?.[1] != state?.selectionBbox?.y1 ||
    last?.[3] != state?.selectionBbox?.y2
  ) {
    lastBboxSelection[workerId] = [
      state?.selectionBbox?.x1 ?? 0,
      state?.selectionBbox?.y1 ?? 0,
      state?.selectionBbox?.x2 ?? 0,
      state?.selectionBbox?.y2 ?? 0
    ];
    handleSelectRemoteAnnotation(
      lastBboxSelection[workerId]!,
      state?.selectionBbox?.isHuman ?? false
    );
  }

  const worker = workers.get(workerId);

  if (!worker) {
    return;
  }

  if (
    ((worker.status !== Status.STATUS_RUNNING && worker.status !== Status.STATUS_STARTING) ||
      worker.deviceId?.toString() !== cameraId?.toString()) &&
    cameraId !== undefined
  ) {
    if (activeWorkers.has(workerId)) {
      const w = activeWorkers.get(workerId);

      if (w) {
        w.reinit();
      }
    }

    const deviceIDHex = toHexFromId<Device['id']>(cameraId)!;
    const livekit = <LiveKitImpl>locateLiveKit(deviceIDHex)!;

    // embedded link recovery: embedded flag enables the stale-map shadow-id fallback (broker.ts:574).
    const track = livekit?.videoForAgent(agentId, deviceIDHex.endsWith('0001')).get(deviceIDHex);

    if (!track || !livekit) {
      return;
    }

    const subject = annotationSubjectForAgentCameraAndWorker(
      toIdFromHex(agentId)!,
      cameraId,
      toIdFromHex(workerId)!
    );

    const embeddingBeaconSubject = embeddingBeaconSubjectForAgentCameraAndWorker(
      toIdFromHex(agentId)!,
      cameraId,
      toIdFromHex(workerId)!
    );

    void worker.start(cameraId, agentId, track, livekit, subject, embeddingBeaconSubject);

    activeWorkers.set(workerId, worker);
  } else if (worker.status === Status.STATUS_RUNNING && cameraId === undefined) {
    console.log('Stopping worker');
    worker.reinit();
    activeWorkers.delete(workerId);
  }
};

export const autoTrackingAnnotations: Record<string, T_Maybe<Annotation[]>> = {};
/**
 * Updates the room state for a specific auto tracking annotations.
 *
 * @param agentId - Agent identifier.
 * @param cameraId - Camera identifier.
 * @param annotations - Auto tracking annotations.
 */
export const synchronizeAutoTrackingAnnotations = (
  _agentId: Agent['id'],
  cameraId: ByteId,
  annotations: T_Maybe<Annotation[]>
): void => {
  const id = toHexFromId<Device['id']>(cameraId);

  autoTrackingAnnotations[id!] = annotations;
};
